<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import DetailedJobAttachmentsDescListItem from '@/components/items/DetailedJobAttachmentsDescListItem.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import { fetchDeleteJobAttachmentById } from '@/helpers/fetchers';

export interface Props {
  attachments?: ReadonlyDeep<openapi.JobDetailedOpenapi['attachments']>;
  refreshDetailedJob?: () => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  attachments: () => [],
  refreshDetailedJob: undefined,
});

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

const loadings = ref<Record<string, boolean>>({});
const _updates = ref<openapi.JobDetailedOpenapi['attachments']>();

async function handleDeleteAttachment(attachmentId: number): Promise<void> {
  if (loadings.value[attachmentId]) return;
  loadings.value[attachmentId] = true;
  await fetchDeleteJobAttachmentById(attachmentId)
    .then(async () => {
      if (props.refreshDetailedJob) {
        await props.refreshDetailedJob();
      }
      // TODO doesn't send updated attachment list to the parent
      _updates.value = updatedAttachments.value.filter((attachment) => {
        return attachment.id !== attachmentId;
      });
    })
    .finally(() => {
      loadings.value[attachmentId] = false;
    });
}

watch(
  () => props.attachments,
  (newValue, oldValue) => {
    _updates.value = undefined;
  },
);

const updatedAttachments = computed(() => {
  return _updates.value || props.attachments;
});
</script>

<template>
  <dl
    :class="$style.detailedJobAttachmentsDescList"
    data-testid="detailedJobAttachmentsDescList"
  >
    <template
      v-for="(attachment, attachmentIndex) in updatedAttachments"
      :key="attachment.id"
    >
      <DetailedJobAttachmentsDescListItem
        :term="
          attachment.file && attachment.file.indexOf('/') >= 0
            ? attachment.file.substring(attachment.file.lastIndexOf('/') + 1)
            : attachment.file
        "
        :download-href="attachment.file"
        :class="$style.item"
        :is-delete-fetching="loadings[attachment.id]"
        @delete-attachment="handleDeleteAttachment(attachment.id)"
      >
        <span>
          <KParentTooltip>{{ attachment.created_at }}</KParentTooltip>
          {{ formatDate(attachment.created_at, timeZone) }}
        </span>
      </DetailedJobAttachmentsDescListItem>
    </template>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobAttachmentsDescList {
}
  .item {
    margin: 8px 0;
  }
</style>
